import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowLeft } from 'lucide-react';
import Nav from '@/components/Nav';
import Footer from '@/components/Footer';
import StickyBottomMarquee, { STICKY_MARQUEE_SPACER_CLASS } from '@/components/StickyBottomMarquee';
import { api, fileUrl } from '@/lib/api';
import { MotionProvider, useReveal } from '@/lib/motion';
import { sortSpeakersByLastName } from '@/lib/sortSpeakers';
import MaintenancePage from '@/pages/MaintenancePage';

const FILTERS = ['ALL', 'SPORTS', 'MEDIA', 'INVESTMENT', 'INNOVATION', 'WELLNESS', 'HOSPITALITY', 'ACADEMIA'];

// Word-based title truncation for the mobile speaker-card name line. If
// the speaker's name has more than `max` words, returns the first `max`
// words followed by "…"; otherwise returns the original string unchanged.
// Used on mobile only — desktop/tablet renders the full name.
const truncateWords = (text, max) => {
  if (!text) return '';
  const words = String(text).trim().split(/\s+/);
  if (words.length <= max) return text;
  return words.slice(0, max).join(' ') + '…';
};

function SpeakerCard({ sp, delay, revealed, simple }) {
  const photo = fileUrl(sp.photo) || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80&auto=format&fit=crop';
  const targetSlug = sp.slug || sp.id;

  return (
    <Link
      to={`/speakers/${targetSlug}`}
      onClick={() => {
        // v3.6 save-on-click: synchronously persist the current scroll
        // position so ScrollToTop's POP-restore handler can return the
        // user here on browser-back from the detail page.
        try { sessionStorage.setItem('scrollPos:' + window.location.pathname, String(window.scrollY)); } catch (_) { /* ignore */ }
      }}
      className={`group relative overflow-hidden rounded-md border border-border-line bg-card-dark motion-safe:transition-transform motion-safe:duration-300 motion-safe:hover:-translate-y-1 block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas ${simple ? 'reveal-simple' : 'reveal-card'} ${revealed ? 'is-in' : ''}`}
      style={{ aspectRatio: '3/4', transitionDelay: revealed ? `${delay}ms` : '0ms' }}
      data-testid="speaker-card"
    >
      <img src={photo} alt={sp.name} className="absolute inset-0 w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-[400ms] ease-out group-hover:scale-[1.04]" />

      <div className="absolute inset-x-0 bottom-0 translate-y-2 opacity-90 group-hover:translate-y-0 group-hover:opacity-100 motion-safe:transition-all motion-safe:duration-[300ms] ease-out">
        <div className="px-4 py-4" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.92) 60%, rgba(0,0,0,0))' }}>
          {/* Name — mobile: word-truncated + forced single line to prevent
              overflow onto the photo. Tapping the card navigates to the
              detail page via the parent <Link>. */}
          <div
            className="md:hidden font-display font-semibold uppercase text-white text-[14px] tracking-tightish truncate"
            data-testid="speaker-card-name-mobile"
          >
            {truncateWords(sp.name, 4)}
          </div>
          {/* Name — tablet/desktop: untouched (full text, identical to pre-fix). */}
          <div className="hidden md:block font-display font-semibold uppercase text-white text-[14px] tracking-tightish">
            {sp.name}
          </div>
          {/* Title — mobile: line-clamp-1 to prevent overflow. Desktop/tablet:
              line-clamp-none (full wrapping text, identical to pre-fix). */}
          <div
            className="font-body text-[11px] text-white/80 mt-1 line-clamp-1 md:line-clamp-none"
            data-testid="speaker-card-title"
          >
            {sp.title}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Speakers() {
  const [settings, setSettings] = useState({});
  const [bottomMarquee, setBottomMarquee] = useState({});
  const [speakers, setSpeakers] = useState(null);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState('ALL');
  const headerReveal = useReveal();
  const gridReveal = useReveal();

  useEffect(() => {
    api.get('/public/home').then((r) => {
      setSettings(r.data?.settings || {});
      setBottomMarquee(r.data?.bottom_marquee || {});
    }).catch(() => {});
    api.get('/speakers')
      .then((r) => setSpeakers(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setErr(e?.message || 'failed'));
  }, []);

  const filtered = useMemo(() => {
    if (!speakers) return [];
    // v3.16 (Round 2, 2C) — Alphabetical-by-last-name for the full
    // directory as well.  Category filter runs first (unchanged
    // membership rules), then the same shared comparator alphabetises
    // whatever remains.  Sorting is display-only; the API response
    // is never mutated.
    const base = active === 'ALL'
      ? speakers
      : speakers.filter((s) => (s.categories || []).map((c) => c.toUpperCase()).includes(active));
    return sortSpeakersByLastName(base);
  }, [speakers, active]);

  const maintenance = settings.maintenance || {};
  if (maintenance.enabled) return <MaintenancePage maintenance={maintenance} />;

  const motion = settings.motion || {};
  const nav = settings.nav || {};
  const footer = settings.footer || {};

  return (
    <MotionProvider motion={motion}>
      <div className={`bg-bg-canvas min-h-screen flex flex-col ${STICKY_MARQUEE_SPACER_CLASS}`}>
        <Nav nav={nav} motion={motion} />
        <div className="sticky top-[72px] z-30 bg-bg-canvas border-b border-ink-primary/10" data-testid="page-back-bar">
          <div className="container-x py-3">
            <Link
              to="/"
              state={{ restoreScroll: true }}
              className="inline-flex items-center gap-2 text-ink-primary/70 hover:text-ink-primary font-body text-[13px] md:text-[14px] min-h-[44px] motion-safe:transition-colors motion-safe:duration-200 focus:outline-none focus-visible:underline"
              data-testid="page-back-link"
            >
              <ArrowLeft size={14} aria-hidden="true" /> Back to Home
            </Link>
          </div>
        </div>
        <main className="flex-1" data-testid="page-speakers">
          <section className="section-pad relative">
            <div className="container-x">
              <div ref={headerReveal.ref} className={`mt-2 text-center ${headerReveal.simple ? 'reveal-simple' : 'reveal-up'} ${headerReveal.revealed ? 'is-in' : ''}`}>
                <p className="eyebrow" data-testid="speakers-page-eyebrow">THE PEOPLE</p>
                <h1 className="mt-4 font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[40px] md:text-[56px] lg:text-[72px]" data-testid="speakers-page-headline">
                  SPEAKERS
                </h1>
                <div className="mt-4 mx-auto w-16 h-[3px] bg-brand-orange" aria-hidden="true" />
                <p className="mt-5 font-body text-[14px] md:text-[15px] text-ink-primary/70 max-w-[640px] mx-auto leading-relaxed" data-testid="speakers-page-subhead">
                  Meet the leaders shaping SEICon III.
                </p>
              </div>

              <div className="relative mt-10">
                <div className="flex gap-2 overflow-x-auto no-scrollbar justify-start lg:justify-center pb-2 pr-12 md:pr-0" data-testid="speaker-filters-page">
                  {FILTERS.map((f) => (
                    <button
                      key={f}
                      onClick={() => setActive(f)}
                      className={
                        'shrink-0 font-body font-semibold text-[12px] uppercase tracking-wider2 px-[18px] py-[10px] rounded-full motion-safe:transition ' +
                        (active === f
                          ? 'bg-brand-orange text-white border border-brand-orange'
                          : 'bg-transparent text-ink-primary border border-border-line hover:border-ink-primary')
                      }
                      data-testid={`filter-page-${f.toLowerCase()}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <div className="pointer-events-none absolute right-0 top-0 h-full flex items-center pl-3 pr-1 bg-gradient-to-l from-bg-canvas via-bg-canvas to-transparent md:hidden" aria-hidden="true">
                  <ChevronRight className="w-5 h-5 text-ink-primary" />
                </div>
              </div>

              <div ref={gridReveal.ref} className="mt-10">
                {speakers === null && !err && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5" data-testid="speakers-loading">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div key={i} className="rounded-md bg-ink-primary/10 animate-pulse" style={{ aspectRatio: '3/4' }} />
                    ))}
                  </div>
                )}

                {err && speakers === null && (
                  <p className="font-body text-[14px] text-ink-primary/70 text-center py-10" data-testid="speakers-error">
                    Unable to load speakers. Please refresh the page.
                  </p>
                )}

                {Array.isArray(speakers) && filtered.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5" data-testid="speakers-page-grid">
                    {filtered.map((sp, i) => {
                      const cols = 5;
                      const row = Math.floor(i / cols);
                      const colIdx = i % cols;
                      const delay = row * 60 + colIdx * 30;
                      return (
                        <SpeakerCard
                          key={sp.id}
                          sp={sp}
                          delay={delay}
                          revealed={gridReveal.revealed}
                          simple={gridReveal.simple}
                        />
                      );
                    })}
                  </div>
                )}

                {Array.isArray(speakers) && filtered.length === 0 && !err && (
                  <p className="font-body text-ink-muted text-center py-10" data-testid="speakers-empty">No speakers match this filter yet.</p>
                )}
              </div>
            </div>
          </section>
        </main>
        <Footer footer={footer} motion={motion} presentingSponsor={settings.presenting_sponsor || null} />
        <StickyBottomMarquee bottomMarquee={bottomMarquee} />
      </div>
    </MotionProvider>
  );
}
